import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PaymentTypesResponse } from '@matix/types';
import { paymentAPI } from '@matix/api-client';

/**
 * Query key for the tenant's payment methods (Inv_PaymentType). Shared so the
 * login-time warm-up and the PaymentModal read/write the exact same cache
 * entry.
 */
export const PAYMENT_TYPES_QUERY_KEY = ['payment-types'] as const;

/**
 * Fetch + cache the tenant's payment methods once per authenticated session.
 *
 * Mount this near the top of the app shell (LegacyApp) with `enabled` set to
 * the auth state so the list is already warm by the time the cashier hits
 * "Charge" — otherwise the payment modal fired the request only on first open,
 * so the methods grid sat on "Loading methods…" for a full round-trip on the
 * first sale of the session.
 *
 * Ops change payment methods in the ERP back-office, not mid-shift, so a long
 * staleTime is safe; the list refreshes on the next session login.
 */
export function usePaymentTypesWarm(options?: {
  enabled?: boolean;
}): UseQueryResult<PaymentTypesResponse, Error> {
  return useQuery<PaymentTypesResponse, Error>({
    queryKey: PAYMENT_TYPES_QUERY_KEY,
    queryFn: () => paymentAPI.listTypes(),
    enabled: options?.enabled ?? true,
    staleTime: 10 * 60 * 1000, // 10 min — matches the PaymentModal's own query
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
  });
}
